import {
  ApiResponse,
  AuthResponse,
  LoginCredentials,
  User,
  CreateUserForm,
  CreateClientForm,
  UpdateUserForm,
  PaginatedResponse,
  PaginationParams,
  SubscriptionPlan,
  CreatePlanForm,
  Feature,
  ClientSubscription,
  AdminDashboardStats,
  ResellerDashboardStats,
  ClientDashboardStats,
  ActivityLog,
  CommissionTransaction,
  GeminiResponse,
  OperationMode
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

class ApiService {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('auth_token');
    }
    return this.token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const token = this.getToken();
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || data.message || 'Request failed',
        };
      }

      return {
        success: true,
        data: data.data || data,
        message: data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  // ============================================
  // AUTHENTICATION
  // ============================================

  async login(credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> {
    const response = await this.request<AuthResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    if (response.success && response.data) {
      this.setToken(response.data.token);
    }

    return response;
  }

  async logout(): Promise<ApiResponse<void>> {
    const response = await this.request<void>('/api/auth/logout', {
      method: 'POST',
    });
    this.setToken(null);
    return response;
  }

  async logoutAllSessions(): Promise<ApiResponse<{ message: string }>> {
    const response = await this.request<{ message: string }>('/api/auth/logout-all', {
      method: 'POST',
    });
    this.setToken(null);
    return response;
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    return this.request<User>('/api/auth/me');
  }

  async refreshToken(): Promise<ApiResponse<AuthResponse>> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      return { success: false, error: 'No refresh token' };
    }

    const response = await this.request<AuthResponse>('/api/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });

    if (response.success && response.data) {
      this.setToken(response.data.token);
      localStorage.setItem('refresh_token', response.data.refreshToken);
    }

    return response;
  }

  // ============================================
  // ADMIN - USERS
  // ============================================

  async getUsers(params?: PaginationParams & { role?: string; status?: string }): Promise<ApiResponse<PaginatedResponse<User>>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));
    if (params?.search) queryParams.set('search', params.search);
    if (params?.role) queryParams.set('role', params.role);
    if (params?.status) queryParams.set('status', params.status);
    if (params?.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.set('sortOrder', params.sortOrder);

    return this.request<PaginatedResponse<User>>(`/api/admin/users?${queryParams}`);
  }

  async createUser(data: CreateUserForm): Promise<ApiResponse<User>> {
    return this.request<User>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id: string, data: UpdateUserForm): Promise<ApiResponse<User>> {
    return this.request<User>(`/api/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/admin/users/${id}`, {
      method: 'DELETE',
    });
  }

  async approveUser(id: string): Promise<ApiResponse<User>> {
    return this.request<User>(`/api/admin/users/${id}/approve`, {
      method: 'POST',
    });
  }

  async suspendUser(id: string, reason?: string): Promise<ApiResponse<User>> {
    return this.request<User>(`/api/admin/users/${id}/suspend`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  // ============================================
  // ADMIN - SUBSCRIPTION PLANS
  // ============================================

  async getPlans(): Promise<ApiResponse<SubscriptionPlan[]>> {
    return this.request<SubscriptionPlan[]>('/api/admin/plans');
  }

  async createPlan(data: CreatePlanForm): Promise<ApiResponse<SubscriptionPlan>> {
    return this.request<SubscriptionPlan>('/api/admin/plans', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePlan(id: string, data: Partial<CreatePlanForm>): Promise<ApiResponse<SubscriptionPlan>> {
    return this.request<SubscriptionPlan>(`/api/admin/plans/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deletePlan(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/admin/plans/${id}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // ADMIN - FEATURES
  // ============================================

  async getFeatures(): Promise<ApiResponse<Feature[]>> {
    return this.request<Feature[]>('/api/admin/features');
  }

  async updateFeature(id: string, data: Partial<Feature>): Promise<ApiResponse<Feature>> {
    return this.request<Feature>(`/api/admin/features/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // ============================================
  // ADMIN - DASHBOARD & ACTIVITY
  // ============================================

  async getAdminDashboard(): Promise<ApiResponse<AdminDashboardStats>> {
    return this.request<AdminDashboardStats>('/api/admin/dashboard');
  }

  async getActivityLogs(params?: PaginationParams & { userId?: string; action?: string }): Promise<ApiResponse<PaginatedResponse<ActivityLog>>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));
    if (params?.userId) queryParams.set('userId', params.userId);
    if (params?.action) queryParams.set('action', params.action);

    return this.request<PaginatedResponse<ActivityLog>>(`/api/admin/activity?${queryParams}`);
  }

  async getAllCommissions(params?: PaginationParams & { status?: string }): Promise<ApiResponse<PaginatedResponse<CommissionTransaction>>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));
    if (params?.status) queryParams.set('status', params.status);

    return this.request<PaginatedResponse<CommissionTransaction>>(`/api/admin/commissions?${queryParams}`);
  }

  async approveCommission(id: string): Promise<ApiResponse<CommissionTransaction>> {
    return this.request<CommissionTransaction>(`/api/admin/commissions/${id}/approve`, {
      method: 'POST',
    });
  }

  async markCommissionPaid(id: string, payoutReference: string): Promise<ApiResponse<CommissionTransaction>> {
    return this.request<CommissionTransaction>(`/api/admin/commissions/${id}/paid`, {
      method: 'POST',
      body: JSON.stringify({ payoutReference }),
    });
  }

  // ============================================
  // RESELLER
  // ============================================

  async getResellerDashboard(): Promise<ApiResponse<ResellerDashboardStats>> {
    return this.request<ResellerDashboardStats>('/api/reseller/dashboard');
  }

  async getResellerClients(params?: PaginationParams & { status?: string }): Promise<ApiResponse<PaginatedResponse<User & { subscription?: ClientSubscription }>>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));
    if (params?.search) queryParams.set('search', params.search);
    if (params?.status) queryParams.set('status', params.status);

    return this.request<PaginatedResponse<User & { subscription?: ClientSubscription }>>(`/api/reseller/clients?${queryParams}`);
  }

  async createClient(data: CreateClientForm): Promise<ApiResponse<User>> {
    return this.request<User>('/api/reseller/clients', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deactivateClient(id: string): Promise<ApiResponse<User>> {
    return this.request<User>(`/api/reseller/clients/${id}/deactivate`, {
      method: 'PATCH',
    });
  }

  async getResellerCommissions(params?: PaginationParams): Promise<ApiResponse<PaginatedResponse<CommissionTransaction>>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));

    return this.request<PaginatedResponse<CommissionTransaction>>(`/api/reseller/commissions?${queryParams}`);
  }

  // ============================================
  // CLIENT
  // ============================================

  async getClientDashboard(): Promise<ApiResponse<ClientDashboardStats>> {
    return this.request<ClientDashboardStats>('/api/client/dashboard');
  }

  async getClientSubscription(): Promise<ApiResponse<ClientSubscription & { plan: SubscriptionPlan }>> {
    return this.request<ClientSubscription & { plan: SubscriptionPlan }>('/api/client/subscription');
  }

  async getAvailableFeatures(): Promise<ApiResponse<string[]>> {
    return this.request<string[]>('/api/client/features');
  }

  // ============================================
  // GEO-PRO ENGINE
  // ============================================

  async processEngineRequest(mode: OperationMode, input: string): Promise<ApiResponse<GeminiResponse>> {
    return this.request<GeminiResponse>('/api/client/engine', {
      method: 'POST',
      body: JSON.stringify({ mode, input }),
    });
  }

  async generateImage(prompt: string): Promise<ApiResponse<{ imageUrl: string }>> {
    return this.request<{ imageUrl: string }>('/api/client/engine/image', {
      method: 'POST',
      body: JSON.stringify({ prompt }),
    });
  }

  async generateVideo(prompt: string): Promise<ApiResponse<{ videoUrl: string }>> {
    return this.request<{ videoUrl: string }>('/api/client/engine/video', {
      method: 'POST',
      body: JSON.stringify({ prompt }),
    });
  }

  // ============================================
  // USAGE TRACKING
  // ============================================

  async getUsageHistory(params?: PaginationParams): Promise<ApiResponse<PaginatedResponse<any>>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));

    return this.request<PaginatedResponse<any>>(`/api/client/usage?${queryParams}`);
  }

  async getUsageLimits(): Promise<ApiResponse<{ daily: any; monthly: any }>> {
    return this.request<{ daily: any; monthly: any }>('/api/client/usage/limits');
  }

  async getUsageSummary(days?: number): Promise<ApiResponse<any[]>> {
    const queryParams = days ? `?days=${days}` : '';
    return this.request<any[]>(`/api/client/usage/summary${queryParams}`);
  }

  // ============================================
  // RESELLER PLANS (for client creation)
  // ============================================

  async getResellerPlans(): Promise<ApiResponse<SubscriptionPlan[]>> {
    return this.request<SubscriptionPlan[]>('/api/reseller/plans');
  }

  async getResellerProfile(): Promise<ApiResponse<any>> {
    return this.request<any>('/api/reseller/profile');
  }

  async updateResellerProfile(data: any): Promise<ApiResponse<any>> {
    return this.request<any>('/api/reseller/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getCommissionSummary(): Promise<ApiResponse<any>> {
    return this.request<any>('/api/reseller/commissions/summary');
  }

  // ============================================
  // ADMIN - SUBSCRIPTIONS
  // ============================================

  async getSubscriptions(params?: PaginationParams & { status?: string; planId?: string }): Promise<ApiResponse<PaginatedResponse<any>>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));
    if (params?.status) queryParams.set('status', params.status);

    return this.request<PaginatedResponse<any>>(`/api/admin/subscriptions?${queryParams}`);
  }

  // ============================================
  // PUBLIC
  // ============================================

  async getPublicPlans(): Promise<ApiResponse<SubscriptionPlan[]>> {
    return this.request<SubscriptionPlan[]>('/api/public/plans');
  }

  async getPublicFeatures(): Promise<ApiResponse<Feature[]>> {
    return this.request<Feature[]>('/api/public/features');
  }

  async getFAQ(): Promise<ApiResponse<Array<{ question: string; answer: string }>>> {
    return this.request<Array<{ question: string; answer: string }>>('/api/public/faq');
  }

  async submitContactForm(data: {
    name: string;
    email: string;
    phone?: string;
    company?: string;
    message: string;
    inquiryType?: string;
  }): Promise<ApiResponse<{ id: string; receivedAt: string }>> {
    return this.request<{ id: string; receivedAt: string }>('/api/public/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getSystemStatus(): Promise<ApiResponse<any>> {
    return this.request<any>('/api/public/status');
  }

  // ============================================
  // PASSWORD & PROFILE
  // ============================================

  async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse<void>> {
    return this.request<void>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async updateProfile(data: { firstName?: string; lastName?: string; phone?: string }): Promise<ApiResponse<User>> {
    return this.request<User>('/api/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getProfile(): Promise<ApiResponse<User>> {
    return this.request<User>('/api/auth/me');
  }

  // ============================================
  // ADMIN - CLIENT SUBSCRIPTION MANAGEMENT
  // ============================================

  async changeClientPlan(userId: string, planId: string, paymentReference?: string): Promise<ApiResponse<{
    message: string;
    subscription: {
      id: string;
      planId: string;
      planName: string;
      status: string;
      startsAt: string;
      expiresAt: string;
    };
  }>> {
    return this.request(`/api/admin/users/${userId}/change-plan`, {
      method: 'POST',
      body: JSON.stringify({ planId, paymentReference }),
    });
  }

  // ============================================
  // ADMIN - RESELLER MANAGEMENT
  // ============================================

  async getResellers(params?: PaginationParams & { status?: string }): Promise<ApiResponse<PaginatedResponse<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    companyName: string;
    phone: string;
    status: string;
    commissionRate: number;
    clientCount: number;
    totalEarnings: number;
    pendingEarnings: number;
    createdAt: string;
  }>>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));
    if (params?.search) queryParams.set('search', params.search);
    if (params?.status) queryParams.set('status', params.status);

    return this.request(`/api/admin/resellers?${queryParams}`);
  }

  async getResellerDetails(id: string): Promise<ApiResponse<{
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      companyName: string;
      phone: string;
      status: string;
      createdAt: string;
    };
    profile: {
      id: string;
      companyName: string;
      companyAddress: string;
      tradeLicense: string;
      commissionRate: number;
      payoutMethod: string;
      payoutDetails: any;
    } | null;
    stats: {
      totalClients: number;
      activeClients: number;
      totalCommission: number;
      pendingCommission: number;
      paidCommission: number;
    };
  }>> {
    return this.request(`/api/admin/resellers/${id}/profile`);
  }

  async updateResellerCommissionRate(id: string, commissionRate: number): Promise<ApiResponse<{
    message: string;
    resellerId: string;
    oldRate?: number;
    newRate: number;
  }>> {
    return this.request(`/api/admin/resellers/${id}/commission-rate`, {
      method: 'PATCH',
      body: JSON.stringify({ commissionRate }),
    });
  }

  // ============================================
  // BKASH PAYMENT
  // ============================================

  async initiatePayment(data: {
    planId: string;
    clientEmail?: string;
    clientData?: {
      id?: string;
      email: string;
      password?: string;
      firstName?: string;
      lastName?: string;
      phone?: string;
      companyName?: string;
    };
    resellerId?: string;
  }): Promise<ApiResponse<{
    paymentId: string;
    bkashURL: string;
    amount: string;
    invoiceNumber: string;
    currency: string;
    sandbox: boolean;
    transactionId: string;
  }>> {
    return this.request('/api/payment/initiate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async executePayment(paymentId: string): Promise<ApiResponse<{
    paymentId: string;
    trxId: string;
    status: string;
    amount: string;
    customerMsisdn?: string;
    sandbox: boolean;
  }>> {
    return this.request('/api/payment/execute', {
      method: 'POST',
      body: JSON.stringify({ paymentId }),
    });
  }

  async getPaymentStatus(paymentId: string): Promise<ApiResponse<{
    paymentId: string;
    status: string;
    amount: string;
    invoiceNumber: string;
    trxId?: string;
    sandbox: boolean;
  }>> {
    return this.request(`/api/payment/status/${paymentId}`);
  }

  async completeSubscription(data: {
    paymentId: string;
    clientData?: {
      email: string;
      password?: string;
      firstName?: string;
      lastName?: string;
      phone?: string;
      companyName?: string;
    };
    resellerId?: string;
  }): Promise<ApiResponse<{
    message: string;
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      status: string;
    };
    subscription: {
      id: string;
      planId: string;
      planName: string;
      status: string;
      startsAt: string;
      expiresAt: string;
    };
    payment: {
      paymentId: string;
      trxId: string;
      amount: number;
    };
  }>> {
    return this.request('/api/payment/complete-subscription', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async simulateSandboxPayment(paymentId: string): Promise<ApiResponse<{
    paymentId: string;
    trxId: string;
    status: string;
    message: string;
  }>> {
    return this.request('/api/payment/sandbox/complete', {
      method: 'POST',
      body: JSON.stringify({ paymentId }),
    });
  }

  async getPaymentPlans(): Promise<ApiResponse<SubscriptionPlan[]>> {
    return this.request<SubscriptionPlan[]>('/api/payment/plans');
  }
}

export const api = new ApiService();
export default api;
